export type ShiftCloseGatewayOptions = {
  baseUrl: string;
  timeoutMs?: number;
};

export type ShiftCloseDenomination = {
  denomination_code: string;
  denomination_value: number;
  denomination_kind: "NOTE" | "COIN";
  display_label: string;
  sort_order: number;
};

export type ShiftCloseRuntime = {
  ok: boolean;
  contract: string;
  has_active_shift: boolean;
  shift: {
    id: string;
    shift_status: "OPEN" | "CLOSING";
    opened_at: string | null;
    is_responsible_actor: boolean;
  } | null;
  currency: {
    currency_code: string;
    minor_unit: number;
  };
  denominations: ShiftCloseDenomination[];
};

export type ShiftCloseCountLine = {
  denomination_code: string;
  retained_quantity: number;
  pending_deposit_quantity: number;
};

export type ShiftCloseCountResult = {
  ok: boolean;
  contract: string;
  round_no: 1 | 2;
  accepted: boolean;
  recount_required: boolean;
  confirmed: boolean;
  confirmation_event_id?: string | null;
  actual_cash?: number | null;
  retained_float?: number | null;
  pending_deposit?: number | null;
  idempotent_replay?: boolean;
};

export type ShiftCloseCommitResult = {
  ok: boolean;
  contract: string;
  shift: {
    id: string;
    shift_status: "CLOSED";
    closed_at: string | null;
    actual_cash: number;
    retained_float: number;
    pending_deposit: number;
    currency_code: string;
  };
  idempotent_replay?: boolean;
};

export type ShiftCloseLifecycleResult = {
  ok: boolean;
  contract: string;
  shift: {
    id: string;
    shift_status: "OPEN" | "CLOSING";
  };
  idempotent_replay?: boolean;
};

export type ShiftCloseGatewayResult<T> = {
  ok: boolean;
  data?: T;
  error?: string;
};

export class PosShiftCloseGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: ShiftCloseGatewayOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 12_000;
  }

  runtime(deviceSecret: string, sessionToken: string) {
    return this.post<ShiftCloseRuntime>("/api/pos/shift/close/runtime", {
      device_secret: deviceSecret,
      session_token: sessionToken,
    });
  }

  begin(input: {
    deviceSecret: string;
    sessionToken: string;
    requestKey: string;
    clientContext?: Record<string, unknown>;
  }) {
    return this.post<ShiftCloseLifecycleResult>("/api/pos/shift/close/begin", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  cancel(input: {
    deviceSecret: string;
    sessionToken: string;
    requestKey: string;
    reason?: string | null;
  }) {
    return this.post<ShiftCloseLifecycleResult>("/api/pos/shift/close/cancel", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      request_key: input.requestKey,
      reason: input.reason ?? null,
    });
  }

  count(input: {
    deviceSecret: string;
    sessionToken: string;
    attemptKey: string;
    roundNo: 1 | 2;
    lines: ShiftCloseCountLine[];
    requestKey: string;
    clientContext?: Record<string, unknown>;
  }) {
    return this.post<ShiftCloseCountResult>("/api/pos/shift/close/count", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      attempt_key: input.attemptKey,
      round_no: input.roundNo,
      lines: input.lines,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  commit(input: {
    deviceSecret: string;
    sessionToken: string;
    confirmationEventId: string;
    requestKey: string;
    clientContext?: Record<string, unknown>;
  }) {
    return this.post<ShiftCloseCommitResult>("/api/pos/shift/close/commit", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      confirmation_event_id: input.confirmationEventId,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  private async post<T>(path: string, body: Record<string, unknown>): Promise<ShiftCloseGatewayResult<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
        credentials: "omit",
      });
      const payload = (await response.json()) as ShiftCloseGatewayResult<T>;
      if (!response.ok) {
        return { ok: false, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return payload;
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof DOMException && error.name === "AbortError"
            ? "GATEWAY_TIMEOUT"
            : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
